// src/user/InviteFriends.js
import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { API } from "../config/apiConfig";
import {
  Paper,
  Typography,
  Button,
  Stack,
  Snackbar,
  Alert,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Grid,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import HistoryIcon from "@mui/icons-material/History";
import ProfessionalTable from "../components/ProfessionalTable";
import api from "../utils/api";
import DashboardPageHeader from "../components/DashboardPageHeader";
import Diversity3Icon from "@mui/icons-material/Diversity3";

export default function InviteFriends() {
  const [referralCode, setReferralCode] = useState("");
  const [referralLink, setReferralLink] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null); // Track which link was copied
  const [userName, setUserName] = useState("");
  
  // Email invitation state
  const [emailInput, setEmailInput] = useState("");
  const [emailList, setEmailList] = useState([]);
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  
  // Invite status state
  const [inviteStatus, setInviteStatus] = useState({
    level1Count: 0,
    remainingInvites: null, // null if exempt
    isExempt: false,
    limit: 10,
    loading: true,
  });

  // Users by level state
  const [usersByLevel, setUsersByLevel] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState("");

  // Reassignment state
  const [level1Referrals, setLevel1Referrals] = useState([]);
  const [loadingLevel1, setLoadingLevel1] = useState(false);
  const [level1Error, setLevel1Error] = useState("");
  const [selectedLevel1User, setSelectedLevel1User] = useState(null);
  const [selectedNewParent, setSelectedNewParent] = useState(null);
  const [reassigning, setReassigning] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [reassignResult, setReassignResult] = useState(null);

  // Reassignment history state
  const [reassignmentHistory, setReassignmentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");

  // Fetch invite status
  const fetchInviteStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("⚠️ No token found, skipping invite status fetch");
        return;
      }

      console.log("📊 Fetching invite status from:", API.getInviteStatus);
      const response = await axios.get(API.getInviteStatus, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("📊 Invite Status Response (full):", response);
      console.log("📊 Invite Status Response (data):", response.data);

      if (response.data.success) {
        console.log("✅ Invite Status Response (success):", response.data);
        const newStatus = {
          ...response.data,
          loading: false,
        };
        console.log("✅ Setting invite status to:", newStatus);
        setInviteStatus(newStatus);
      } else {
        console.error("❌ Invite status response not successful:", response.data);
        setInviteStatus(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error("❌ Error fetching invite status:", err);
      console.error("❌ Error response:", err.response?.data);
      setInviteStatus(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No token found. Please log in again.");
        return;
      }

      const decoded = jwtDecode(token);
      console.log("Decoded JWT in React:", decoded);

      if (decoded.referralCode) {
        setReferralCode(decoded.referralCode);
        // Generate full referral link
        const link = `https://dv-apps.com/signup?ref=${decoded.referralCode}`;
        setReferralLink(link);
        
        // Get user name from token
        const name = [decoded.firstName, decoded.lastName].filter(Boolean).join(' ') || decoded.userName || 'A friend';
        setUserName(name);
        setError(""); // Clear any previous errors if we successfully got the referral code
      } else {
        setError("Referral code missing in token");
      }

      // Fetch invite status
      fetchInviteStatus();
      
      // Fetch users by level
      fetchUsersByLevel();
      
      // Fetch Level 1 referrals for reassignment
      fetchLevel1ReferralsForReassignment();
      
      // Fetch reassignment history
      fetchReassignmentHistory();
    } catch (err) {
      console.error("❌ InviteFriends error:", err);
      setError("Failed to read referral code from token");
    }
  }, []);

  // Fetch users by level
  const fetchUsersByLevel = async () => {
    setLoadingUsers(true);
    setUsersError("");
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("⚠️ No token found, skipping users by level fetch");
        return;
      }

      const response = await api.get(API.getUsersByLevel);
      
      if (response.data.success) {
        setUsersByLevel(response.data.users || []);
      } else {
        setUsersError("Failed to fetch users");
      }
    } catch (err) {
      console.error("❌ Error fetching users by level:", err);
      setUsersError(err.response?.data?.error || "Failed to fetch users");
      setUsersByLevel([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch Level 1 referrals for reassignment
  const fetchLevel1ReferralsForReassignment = async () => {
    setLoadingLevel1(true);
    setLevel1Error("");
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("⚠️ No token found, skipping Level 1 referrals fetch");
        setLevel1Error("Please log in to view Level 1 referrals");
        return;
      }

      const apiUrl = API.getLevel1ReferralsForReassignment;
      console.log("📊 [Frontend] Fetching Level 1 referrals from:", apiUrl);
      
      const response = await api.get(apiUrl);
      
      console.log("📊 [Frontend] Full response object:", response);
      console.log("📊 [Frontend] Response status:", response.status);
      console.log("📊 [Frontend] Response data:", response.data);
      console.log("📊 [Frontend] Response data type:", typeof response.data);
      console.log("📊 [Frontend] Response.data.success:", response.data?.success);
      console.log("📊 [Frontend] Response.data.level1Referrals:", response.data?.level1Referrals);
      console.log("📊 [Frontend] Response.data.level1Referrals type:", typeof response.data?.level1Referrals);
      console.log("📊 [Frontend] Response.data.level1Referrals length:", response.data?.level1Referrals?.length);
      
      if (response.data && response.data.success === true) {
        const referrals = response.data.level1Referrals || [];
        console.log("✅ [Frontend] Parsed referrals array:", referrals);
        console.log("✅ [Frontend] Referrals array length:", referrals.length);
        console.log("✅ [Frontend] Is array?", Array.isArray(referrals));
        
        if (Array.isArray(referrals) && referrals.length > 0) {
          console.log("✅ [Frontend] First referral sample:", {
            UserID: referrals[0].UserID,
            FullName: referrals[0].FullName,
            ReferralCode: referrals[0].ReferralCode
          });
        }
        
        setLevel1Referrals(referrals);
        console.log("✅ [Frontend] Set level1Referrals state with", referrals.length, "items");
        
        if (referrals.length === 0) {
          console.log("⚠️ [Frontend] No Level 1 referrals found for this user");
          setLevel1Error(""); // Clear error if it's just empty results
        }
      } else {
        const errorMsg = response.data?.error || "Failed to fetch Level 1 referrals";
        console.error("❌ [Frontend] Failed to fetch Level 1 referrals. Response:", response.data);
        console.error("❌ [Frontend] Success value:", response.data?.success);
        setLevel1Error(errorMsg);
        setLevel1Referrals([]);
      }
    } catch (err) {
      console.error("❌ Error fetching Level 1 referrals:", err);
      console.error("❌ Error response:", err.response?.data);
      console.error("❌ Error message:", err.message);
      const errorMsg = err.response?.data?.error || err.message || "Failed to fetch Level 1 referrals";
      setLevel1Error(errorMsg);
      setLevel1Referrals([]);
    } finally {
      setLoadingLevel1(false);
    }
  };

  // Fetch reassignment history
  const fetchReassignmentHistory = async () => {
    setLoadingHistory(true);
    setHistoryError("");
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("⚠️ No token found, skipping reassignment history fetch");
        return;
      }

      console.log("📜 [Frontend] Fetching reassignment history from:", API.getReferralReassignmentHistory);
      const response = await api.get(API.getReferralReassignmentHistory);
      
      console.log("📜 [Frontend] History response:", response);
      console.log("📜 [Frontend] Response data:", response.data);
      
      if (response.data && response.data.success === true) {
        const history = response.data.history || [];
        console.log("✅ [Frontend] Fetched reassignment history:", history.length, "records");
        setReassignmentHistory(history);
        
        if (history.length === 0) {
          console.log("⚠️ [Frontend] No reassignment history found");
        }
      } else {
        const errorMsg = response.data?.error || "Failed to fetch reassignment history";
        console.error("❌ [Frontend] Failed to fetch history:", response.data);
        setHistoryError(errorMsg);
        setReassignmentHistory([]);
      }
    } catch (err) {
      console.error("❌ [Frontend] Error fetching reassignment history:", err);
      console.error("❌ [Frontend] Error response:", err.response?.data);
      const errorMsg = err.response?.data?.error || err.message || "Failed to fetch reassignment history";
      setHistoryError(errorMsg);
      setReassignmentHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Filter users for new parent dropdown (exclude selected Level 1 user and their direct descendants)
  const getFilteredUsersForNewParent = () => {
    if (!selectedLevel1User) {
      return usersByLevel;
    }

    // Get the selected Level 1 user's referral code
    const selectedReferralCode = selectedLevel1User.ReferralCode;
    if (!selectedReferralCode) {
      return usersByLevel;
    }

    // Filter out:
    // 1. The selected Level 1 user itself
    // 2. Direct descendants (users whose ReferredByCode matches the selected user's ReferralCode)
    // Note: Full descendant chain validation is handled by the backend
    return usersByLevel.filter(user => {
      // Exclude the selected Level 1 user
      if (user.UserID === selectedLevel1User.UserID) {
        return false;
      }

      // Exclude direct descendants (Level 2 under the selected Level 1 user)
      if (user.ReferredByCode && 
          user.ReferredByCode.trim().toUpperCase() === selectedReferralCode.trim().toUpperCase()) {
        return false;
      }
      
      return true; // Include all others - backend will validate full descendant chain
    });
  };

  // Handle reassignment submission
  const handleReassignSubmit = async () => {
    if (!selectedLevel1User || !selectedNewParent) {
      return;
    }

    setReassigning(true);
    setReassignResult(null);

    try {
      const response = await api.post(API.reassignLevel1Parent, {
        level1UserId: selectedLevel1User.UserID,
        newParentReferralCode: selectedNewParent.ReferralCode,
      });

      if (response.data.success) {
        setReassignResult({
          success: true,
          message: response.data.message || "Referral reassigned successfully",
        });
        
        // Reset selections
        setSelectedLevel1User(null);
        setSelectedNewParent(null);
        setConfirmDialogOpen(false);
        
        // Refresh data
        await fetchLevel1ReferralsForReassignment();
        await fetchUsersByLevel();
        await fetchReassignmentHistory(); // Refresh history after successful reassignment
      } else {
        setReassignResult({
          success: false,
          message: response.data.error || "Failed to reassign referral",
        });
      }
    } catch (err) {
      console.error("❌ Error reassigning referral:", err);
      setReassignResult({
        success: false,
        message: err.response?.data?.error || err.message || "Failed to reassign referral",
      });
    } finally {
      setReassigning(false);
    }
  };

  const handleCopyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      setCopied(true);
    }
  };

  // Handle copying referral link from table
  const handleCopyTableLink = (referralCode, userId) => {
    if (referralCode) {
      const link = `https://dv-apps.com/signup?ref=${referralCode}`;
      navigator.clipboard.writeText(link);
      setCopiedLinkId(userId);
      setCopied(true);
      // Reset after 2 seconds
      setTimeout(() => {
        setCopiedLinkId(null);
      }, 2000);
    }
  };

  const handleAddEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Check for duplicates
    if (emailList.includes(email)) {
      setError("This email is already in the list");
      return;
    }

    // No limit check - all users can invite unlimited level-1 referrals

    setEmailList([...emailList, email]);
    setEmailInput("");
    setError("");
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmailList(emailList.filter(email => email !== emailToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleSendInvites = async () => {
    if (emailList.length === 0) {
      setError("Please add at least one email address");
      return;
    }

    setSending(true);
    setError("");
    setSendResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        API.sendReferralInvites,
        {
          recipients: emailList,
          message: customMessage || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setSendResult({
          success: true,
          message: response.data.message,
          sent: response.data.sent,
          failed: response.data.failed,
        });
        // Clear form on success
        setEmailList([]);
        setCustomMessage("");
        // Refresh invite status
        await fetchInviteStatus();
      } else {
        setError(response.data.error || "Failed to send invitations");
      }
    } catch (err) {
      console.error("❌ Error sending invitations:", err);
      const errorMessage = err.response?.data?.error || err.message || "Failed to send invitations";
      setError(errorMessage);
      setSendResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, bgcolor: 'transparent' }}>
      <DashboardPageHeader
        icon={Diversity3Icon}
        title="Grow my community"
        subtitle="Share your referral link, invite friends by email, and manage your network assignments."
      />
      <Stack spacing={3} alignItems="stretch">
        {/* Hidden text */}
        {false && (
          <Typography variant="body1" textAlign="center">
            Grow your community by sharing your referral link with friends!
          </Typography>
        )}

        {error && !referralCode && <Alert severity="error">{error}</Alert>}

        {referralCode && (
          <>
            <Box>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 1,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }}
              >
                Your Referral Code:
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: { xs: 2, sm: 3 }, 
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                  wordBreak: 'break-word'
                }}
              >
                <strong>{referralCode}</strong>
              </Typography>
            </Box>

            {/* Two Column Layout: 50% Copy Link, 50% Empty Space */}
            <Grid container spacing={4}>
              {/* Left Section: Copy Link (50%) */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                    }}
                  >
                    Your Referral Link:
                  </Typography>
                  <TextField
                    fullWidth
                    value={referralLink}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleCopyLink}
                            edge="end"
                            color="primary"
                            aria-label="copy referral link"
                            size="small"
                            sx={{ 
                              padding: { xs: '6px', sm: '8px' }
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "background.default",
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCopyLink}
                    startIcon={<ContentCopyIcon />}
                    fullWidth
                    sx={{ 
                      mt: { xs: 1, sm: 2 },
                      py: { xs: 1, sm: 1.25 },
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    Copy Link
                  </Button>
                </Box>
              </Grid>

              {/* Right Section: Reassign Level 1 Referral (50%) */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                      fontWeight: 600
                    }}
                  >
                    Reassign Level 1 Referral
                  </Typography>

                  {/* Error Alert */}
                  {level1Error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLevel1Error("")}>
                      {level1Error}
                    </Alert>
                  )}

                  {/* Level 1 User Selection */}
                  {loadingLevel1 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        Loading Level 1 referrals...
                      </Typography>
                    </Box>
                  ) : level1Referrals.length === 0 ? (
                    <Box 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center', 
                        color: 'text.secondary',
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'action.hover'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        No Level 1 referrals found. You need to have at least one direct referral to use this feature.
                      </Typography>
                    </Box>
                  ) : (
                    <Autocomplete
                      options={level1Referrals}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        const name = option.FullName || option.UserName || 'Unknown';
                        const code = option.ReferralCode || 'N/A';
                        return `${name} (${code})`;
                      }}
                      getOptionKey={(option) => option.UserID || option.ReferralCode || ''}
                      isOptionEqualToValue={(option, value) => {
                        return option.UserID === value.UserID;
                      }}
                      value={selectedLevel1User}
                      onChange={(event, newValue) => {
                        console.log("🔄 Selected Level 1 user:", newValue);
                        setSelectedLevel1User(newValue);
                        setSelectedNewParent(null); // Reset new parent when Level 1 user changes
                      }}
                      loading={loadingLevel1}
                      disabled={loadingLevel1}
                      noOptionsText="No Level 1 referrals available"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Level 1 User"
                          placeholder={level1Referrals.length === 0 ? "No Level 1 referrals" : "Search Level 1 referral..."}
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "background.default",
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => {
                        console.log("🎨 Rendering option:", option);
                        return (
                          <Box component="li" {...props} key={option.UserID || option.ReferralCode}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {option.FullName || option.UserName || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Code: {option.ReferralCode || 'N/A'} • Referrals: {option.TotalReferrals || 0}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }}
                      sx={{ mb: 2 }}
                    />
                  )}

                  {/* New Parent Selection */}
                  <Autocomplete
                    options={getFilteredUsersForNewParent()}
                    getOptionLabel={(option) => {
                      const name = [option.FirstName, option.LastName].filter(Boolean).join(" ") || option.UserName || 'Unknown';
                      return `${name} (Level ${option.Level}, ${option.ReferralCode})`;
                    }}
                    value={selectedNewParent}
                    onChange={(event, newValue) => {
                      setSelectedNewParent(newValue);
                    }}
                    disabled={!selectedLevel1User || loadingUsers}
                    loading={loadingUsers}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select New Parent"
                        placeholder={selectedLevel1User ? "Search user in your tree..." : "Select Level 1 user first"}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "background.default",
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {[option.FirstName, option.LastName].filter(Boolean).join(" ") || option.UserName || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Level {option.Level} • Code: {option.ReferralCode} • Referrals: {option.ReferralCount || 0}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    sx={{ mb: 2 }}
                  />

                  {/* Preview */}
                  {selectedLevel1User && selectedNewParent && (
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        mb: 2, 
                        borderRadius: 1, 
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Preview:
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Moving <strong>{selectedLevel1User.FullName || selectedLevel1User.UserName}</strong> from <strong>{selectedLevel1User.CurrentReferredByCode || 'You'}</strong> to <strong>{[selectedNewParent.FirstName, selectedNewParent.LastName].filter(Boolean).join(" ") || selectedNewParent.UserName}</strong>
                      </Typography>
                    </Box>
                  )}

                  {/* Submit Button */}
                  <Button
                    variant="contained"
                    onClick={() => setConfirmDialogOpen(true)}
                    disabled={!selectedLevel1User || !selectedNewParent || reassigning}
                    startIcon={reassigning ? <CircularProgress size={16} /> : <SwapHorizIcon />}
                    fullWidth
                    sx={{ 
                      py: { xs: 1, sm: 1.25 },
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {reassigning ? "Reassigning..." : "Reassign Parent"}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Email Invitation Section - Hidden */}
            {false && (
              <>
                <Divider sx={{ my: 3 }} />

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    <EmailIcon /> Send Invitations via Email
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter email address and press Enter"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={handleAddEmail}
                                disabled={!emailInput.trim()}
                              >
                                Add
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                        helperText=""
                      />
                    </Box>

                    {emailList.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Recipients ({emailList.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {emailList.map((email, index) => (
                            <Chip
                              key={index}
                              label={email}
                              onDelete={() => handleRemoveEmail(email)}
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <TextField
                      fullWidth
                      label="Message"
                      multiline
                      rows={3}
                      value={userName && referralLink ? `Hi there! ${userName} has invited you to join Dreamy Apps! This is an incredible opportunity to grow your own network, discover powerful apps that can transform your daily experience, and be part of something that truly makes a difference. Join us by clicking this link: ${referralLink}. Let's embark on this journey together and unlock new possibilities!` : ""}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "background.default",
                        },
                      }}
                    />

                    <Button
                      variant="contained"
                      onClick={handleSendInvites}
                      disabled={sending || emailList.length === 0}
                      startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                      sx={{ mt: 2 }}
                    >
                      {sending 
                        ? "Sending..." 
                        : `Send Invitations (${emailList.length})`
                      }
                    </Button>

                    {sendResult && (
                      <Alert 
                        severity={sendResult.success ? "success" : "error"}
                        onClose={() => setSendResult(null)}
                      >
                        {sendResult.message}
                        {sendResult.success && sendResult.failed > 0 && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {sendResult.sent} sent, {sendResult.failed} failed
                          </Typography>
                        )}
                      </Alert>
                    )}
                  </Stack>
                </Box>
              </>
            )}
          </>
        )}

        {/* Reassignment History */}
        <Divider sx={{ my: 3 }} />
        
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Referral Reassignment History
            </Typography>
          </Box>
          
          {historyError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistoryError("")}>
              {historyError}
            </Alert>
          )}

          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : reassignmentHistory.length === 0 ? (
            <Box 
              sx={{ 
                p: 3, 
                textAlign: 'center', 
                color: 'text.secondary',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'action.hover'
              }}
            >
              <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                No reassignment history found. History will appear here after you reassign any Level 1 referrals.
              </Typography>
            </Box>
          ) : (
            <ProfessionalTable
              columns={[
                {
                  header: "Date & Time",
                  accessor: "CreatedAt",
                  headerStyle: { minWidth: "180px" },
                  render: (value) => {
                    if (!value) return "-";
                    const date = new Date(value);
                    return (
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {date.toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {date.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                      </Box>
                    );
                  },
                },
                {
                  header: "User Reassigned",
                  accessor: (row) => row.Level1FullName || row.Level1UserName || 'Unknown',
                  headerStyle: { minWidth: "150px" },
                  render: (value, row) => (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {value}
                      </Typography>
                      {row.Level1ReferralCode && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Code: {row.Level1ReferralCode}
                        </Typography>
                      )}
                    </Box>
                  ),
                },
                {
                  header: "From",
                  accessor: (row) => row.OldParentFullName || row.OldReferredByCode || 'You',
                  headerStyle: { minWidth: "150px" },
                  render: (value, row) => (
                    <Box>
                      <Typography variant="body2">
                        {value}
                      </Typography>
                      {row.OldParentReferralCode && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Code: {row.OldParentReferralCode}
                        </Typography>
                      )}
                    </Box>
                  ),
                },
                {
                  header: "To",
                  accessor: (row) => row.NewParentFullName || row.NewReferredByCode || 'Unknown',
                  headerStyle: { minWidth: "150px" },
                  render: (value, row) => (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                        {value}
                      </Typography>
                      {row.NewParentReferralCode && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Code: {row.NewParentReferralCode}
                        </Typography>
                      )}
                    </Box>
                  ),
                },
                {
                  header: "Comment",
                  accessor: "AutoComment",
                  headerStyle: { minWidth: "300px" },
                  render: (value) => (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        color: 'text.secondary',
                        fontStyle: 'italic'
                      }}
                    >
                      {value || '-'}
                    </Typography>
                  ),
                },
              ]}
              data={reassignmentHistory}
              loading={loadingHistory}
              emptyMessage="No reassignment history found"
            />
          )}
        </Box>

        {/* Users by Level Table */}
        <Divider sx={{ my: 3 }} />
        
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Users by Level (Less than 4 Referrals)
          </Typography>
          
          {usersError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUsersError("")}>
              {usersError}
            </Alert>
          )}

          <ProfessionalTable
            columns={[
              {
                header: "Level",
                accessor: "Level",
                headerStyle: { minWidth: "80px" },
                render: (value) => (
                  <Chip 
                    label={`Level ${value}`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                ),
              },
              {
                header: "Name",
                accessor: (row) => {
                  const name = [row.FirstName, row.LastName].filter(Boolean).join(" ");
                  return name || "-";
                },
                headerStyle: { minWidth: "150px" },
              },
              {
                header: "Referral Link",
                accessor: (row) => row.ReferralCode ? `https://dv-apps.com/signup?ref=${row.ReferralCode}` : "-",
                headerStyle: { minWidth: "250px" },
                render: (value, row) => {
                  if (!row.ReferralCode) return "-";
                  const isCopied = copiedLinkId === row.UserID;
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      >
                        {value}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyTableLink(row.ReferralCode, row.UserID);
                        }}
                        color={isCopied ? "success" : "primary"}
                        sx={{ 
                          padding: "4px",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                        title={isCopied ? "Copied!" : "Copy link"}
                      >
                        <ContentCopyIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </Box>
                  );
                },
              },
              {
                header: "Referrals",
                accessor: "ReferralCount",
                headerStyle: { minWidth: "100px", textAlign: "center" },
                style: { textAlign: "center" },
                render: (value) => (
                  <Chip 
                    label={value} 
                    size="small" 
                    color={value === 0 ? "default" : "secondary"}
                  />
                ),
              },
              {
                header: "Joining Date",
                accessor: "JoiningDate",
                headerStyle: { minWidth: "150px" },
                render: (value) => {
                  if (!value) return "-";
                  const date = new Date(value);
                  return date.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  });
                },
              },
            ]}
            data={usersByLevel}
            loading={loadingUsers}
            emptyMessage="No users found with less than 4 referrals"
          />
        </Box>
      </Stack>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => {
          setCopied(false);
          setCopiedLinkId(null);
        }}
        message="Referral link copied to clipboard!"
      />

      {/* Reassignment Result Snackbar */}
      <Snackbar
        open={!!reassignResult}
        autoHideDuration={6000}
        onClose={() => setReassignResult(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setReassignResult(null)} 
          severity={reassignResult?.success ? "success" : "error"}
          sx={{ width: '100%' }}
        >
          {reassignResult?.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Reassignment
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {selectedLevel1User && selectedNewParent && (
              <>
                Are you sure you want to reassign <strong>{selectedLevel1User.FullName || selectedLevel1User.UserName}</strong> from <strong>{selectedLevel1User.CurrentReferredByCode || 'You'}</strong> to <strong>{[selectedNewParent.FirstName, selectedNewParent.LastName].filter(Boolean).join(" ") || selectedNewParent.UserName}</strong>?
                <br /><br />
                This action will change the referral tree structure. The change will be logged for audit purposes.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={reassigning}>
            Cancel
          </Button>
          <Button 
            onClick={handleReassignSubmit} 
            variant="contained" 
            color="primary"
            disabled={reassigning}
            startIcon={reassigning ? <CircularProgress size={16} /> : null}
          >
            {reassigning ? "Reassigning..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
